<script setup lang="ts">
import { computed } from 'vue';

/**
 * `danger` is its own variant rather than a colour someone remembers to pass,
 * so a destructive action cannot quietly look like an ordinary one.
 */
const props = withDefaults(
	defineProps<{
		variant?: 'primary' | 'secondary' | 'danger';
		type?: 'button' | 'submit';
		disabled?: boolean | undefined;
		/** Shows work in progress and blocks a second click. */
		busy?: boolean | undefined;
		/**
		 * Text rather than a filled control, keeping the variant's colour.
		 *
		 * For an action inside a row. A solid `danger` button repeated down a
		 * table is the loudest thing on the page, which makes the one in the
		 * confirmation that follows it mean less — and a row of them reads as
		 * a warning about the data rather than as something to press.
		 */
		quiet?: boolean | undefined;
	}>(),
	{ variant: 'secondary', type: 'button', disabled: false, busy: false, quiet: false },
);

const base =
	'inline-flex items-center justify-center gap-x-2 rounded-lg text-sm font-medium ' +
	'transition-colors disabled:pointer-events-none disabled:opacity-50';

const variants = {
	primary: 'px-3 py-2 bg-accent text-accent-fg shadow-xs hover:not-disabled:opacity-90',
	// A surface rather than a transparent outline: on a page ground that is no
	// longer white, an unfilled button reads as disabled.
	secondary:
		'px-3 py-2 border-border bg-surface text-fg border shadow-xs hover:not-disabled:bg-bg',
	danger: 'px-3 py-2 bg-danger text-danger-fg shadow-xs hover:not-disabled:opacity-90',
} as const;

/** Named apart from the prop: a `quiet` const would shadow it in the template. */
const quietVariants = {
	primary: 'text-accent hover:not-disabled:bg-accent-bg',
	secondary: 'text-muted hover:not-disabled:bg-border/40',
	danger: 'text-danger hover:not-disabled:bg-danger-bg',
} as const;

const classes = computed(
	() =>
		`${base} ${props.quiet === true ? `px-2 py-1 ${quietVariants[props.variant]}` : variants[props.variant]}`,
);
</script>

<template>
	<button :type="type" :class="classes" :disabled="disabled || busy">
		<slot />
	</button>
</template>
